import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import Icon from '../ui/icon';
import { cn } from '../../lib/utils';

interface ActionCardProps {
  title: string;
  description: string;
  icon: string;
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  badge?: string;
  onClick?: () => void;
  className?: string;
}

const colorVariants = {
  blue: 'border-l-blue-500 bg-blue-100/20 dark:bg-blue-100/10',
  green: 'border-l-green-500 bg-green-100/20 dark:bg-green-100/10',
  purple: 'border-l-purple-500 bg-purple-100/20 dark:bg-purple-100/10',
  orange: 'border-l-orange-500 bg-orange-100/20 dark:bg-orange-100/10',
  red: 'border-l-red-500 bg-red-100/20 dark:bg-red-100/10',
};

const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  icon,
  color = 'blue',
  badge,
  onClick,
  className
}) => {
  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-l-4",
        "hover:border-l-8 hover:bg-opacity-30 dark:hover:bg-opacity-20",
        "active:scale-[0.98] active:shadow-sm",
        colorVariants[color],
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors duration-200">
            <Icon name={icon} size={20} className="text-primary" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-md">{title}</h3>
              {badge && (
                <Badge variant="outline" className="text-xs">
                  {badge}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1">{description}</p>
          </div>
          
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors duration-200">
            <Icon name="chevron-right" size={16} className="text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActionCard;
